import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface ItemOfertaCpf {
  nome: string;
  valor: number;
  gb: number | null;
}

export interface CatalogoOfertaCpf {
  fibra: ItemOfertaCpf | null;
  tv: ItemOfertaCpf | null;
  dependente: ItemOfertaCpf | null;
  convergente: ItemOfertaCpf | null;
}

/**
 * Os produtos novos que a ficha do lead CPF oferece: a fibra, a TV, a linha
 * dependente e o combo convergente.
 *
 * Sai do catálogo CPF do banco, nunca de valor escrito no código — tabela nova da
 * Claro muda o catálogo e a oferta acompanha. De cada categoria pega a entrada
 * mais barata, que é a que abre a conversa; o consultor troca na proposta.
 *
 * ⚠️ A linha dependente não tem GB próprio: ela divide o pacote do titular. Quem
 * tem GB é o pacote adicional de internet, que é outro item do catálogo.
 */
export function usePlanosCpfOferta() {
  return useQuery({
    queryKey: ['planos-cpf-oferta'],
    staleTime: 30 * 60 * 1000,
    queryFn: async (): Promise<CatalogoOfertaCpf> => {
      const { data, error } = await supabase
        .from('planos_proposta_catalogo')
        .select('nome, valor, gb, categoria')
        .eq('tipo_pessoa', 'PF')
        .eq('ativo', true)
        .in('categoria', ['fibra', 'tv', 'adicional_movel', 'convergente']);
      if (error) throw error;

      const linhas = ((data || []) as Array<{ nome: string; valor: number | null; gb: number | null; categoria: string }>)
        .map((r) => ({ ...r, valor: Number(r.valor) || 0, gb: r.gb == null ? null : Number(r.gb) }))
        .filter((r) => r.valor > 0);

      const maisBarato = (categoria: string, filtroNome?: RegExp): ItemOfertaCpf | null => {
        const candidatos = linhas
          .filter((r) => r.categoria === categoria && (!filtroNome || filtroNome.test(r.nome)))
          .sort((a, b) => a.valor - b.valor);
        const escolhido = candidatos[0];
        return escolhido ? { nome: escolhido.nome, valor: escolhido.valor, gb: escolhido.gb } : null;
      };

      return {
        fibra: maisBarato('fibra'),
        tv: maisBarato('tv'),
        dependente: maisBarato('adicional_movel', /dependente/i),
        convergente: maisBarato('convergente'),
      };
    },
  });
}
